import {
  Skeleton,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useContext, useState } from "react";
import { InfoOutlined } from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AuthContext } from "../../context/auth-context";
import { ENTITY_LABELS } from "../../utils/labelMapping";
import { OVERVIEW_LLM_LABELS } from "../../utils/constant";
import BrandAvatar from "../../Component/Avatar/Avatar";
import MUICard from "../../Component/UI/MUICard/MUICard";
import MUIIconButton from "../../Component/UI/MUIIconButton/MUIIconButton";
import Table, { StyledTableCell, StyledTableRow } from "../../Component/UI/Table/Table";
import { extractDomain } from "../../utils/helpers";
import CATabbedModal from "./CATabbedModal";
import CADetailedTableModal from "./CADetailedTableModal";
import classes from "../BrandAudit/CategoryDimensions/CategoryDimensions.module.css";

function getLLMLabel(llm) {
  return OVERVIEW_LLM_LABELS[llm] || llm || "";
}

// Consensus badge — shows how many of the selected LLMs surfaced this attribute
function ConsensusBadge({ llmCount, totalLLMs }) {
  if (!llmCount || !totalLLMs) return null;
  const isAll  = llmCount === totalLLMs;
  const isMost = llmCount > totalLLMs / 2;
  const bg     = isAll ? "#d1fae5" : isMost ? "#dbeafe" : "#f3f4f6";
  const color  = isAll ? "#065f46" : isMost ? "#1e40af" : "#6b7280";
  return (
    <Tooltip title={`Mentioned by ${llmCount} of ${totalLLMs} LLMs`} arrow placement="top">
      <span style={{
        fontSize: "0.58rem", fontWeight: 700,
        padding: "1px 5px", borderRadius: 8,
        background: bg, color, lineHeight: 1.6,
        whiteSpace: "nowrap", flexShrink: 0, cursor: "default",
      }}>
        {llmCount}/{totalLLMs}
      </span>
    </Tooltip>
  );
}

// ── Attribute name synonym groups ──────────────────────────────────────────────
// Each group's first entry is the canonical display name.
// Attribute names matching any synonym in a group are merged together.
const ATTRIBUTE_SYNONYM_GROUPS = [
  ["Safety and Side Effects", "Safety"],
  ["Prescription Requirements and Eligibility", "Eligibility"],
  ["Cost and Insurance Coverage", "Cost"],
];

function getCanonicalAttributeName(name) {
  if (!name) return name;
  const lower = name.trim().toLowerCase();
  for (const group of ATTRIBUTE_SYNONYM_GROUPS) {
    for (const synonym of group) {
      if (synonym.trim().toLowerCase() === lower) return group[0];
    }
  }
  // Substring containment fallback
  for (const group of ATTRIBUTE_SYNONYM_GROUPS) {
    for (const synonym of group) {
      const synLower = synonym.trim().toLowerCase();
      if (lower.includes(synLower) || synLower.includes(lower)) return group[0];
    }
  }
  return name.trim();
}

// Format base-response content for a single LLM in the category dimension modal
function formatCategoryDimensionContent(baseResponsesData, brandName, entity) {
  if (!baseResponsesData || typeof baseResponsesData !== "object") return "No data available.";
  const config = {
    titlePrefix: `${ENTITY_LABELS.purchaseCriteria} - Base Prompt and Responses`,
    showPrompt: true,
    showResponse: true,
  };
  const isMainBrand = brandName?.toLowerCase() === entity?.toLowerCase();
  let content = "";
  let queryCounter = 0;

  const addQuerySection = (data, queryLabel) => {
    if (!data) return;
    queryCounter++;
    if (content) content += "\n\n---\n\n";
    if (queryLabel) content += `## Query ${queryCounter}: ${queryLabel}\n\n`;
    if (config.showPrompt && data.basePrompt) {
      content += `### Base Prompt\n\n${data.basePrompt}\n\n`;
    }
    if (config.showResponse) {
      const baseResContent = data.baseRes
        ? (Array.isArray(data.baseRes) ? data.baseRes.join("\n\n") : data.baseRes)
        : (data.baseResponse
            ? (Array.isArray(data.baseResponse) ? data.baseResponse.join("\n\n") : data.baseResponse)
            : "");
      if (baseResContent) {
        content += `---\n\n### Base Response\n\n${baseResContent}`;
      }
    }
  };

  if (isMainBrand) {
    const criteriaGeneration = baseResponsesData.criteriaGenerationBaseRes;
    if (Array.isArray(criteriaGeneration) && criteriaGeneration.length > 0) {
      criteriaGeneration.forEach((item) => { if (item) addQuerySection(item, "Criteria Generation"); });
    }
  }

  const criteriaRating = baseResponsesData.criteriaRatingBaseRes;
  if (criteriaRating && Array.isArray(criteriaRating)) {
    criteriaRating.forEach((item) => { if (item) addQuerySection(item, "Criteria Rating"); });
  }

  return content || `No data available for ${brandName}.`;
}

/**
 * Composite Purchase Criteria table — shown when "Compare Across → By LLM" is active in CategoryAnalysis.
 * Averages ratings from all selected LLMs for each (normalised) attribute+brand pair.
 * Attributes with same meaning (per ATTRIBUTE_SYNONYM_GROUPS) are merged.
 * Unmatched attributes appear as their own rows.
 */
export default function CAPurchaseCriteriaComposite({ category, entity, ctx, multiLLMs }) {
  const { user } = useContext(AuthContext);
  const isCompanyUser = user?.user_role?.role?.name === "Company user";

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [tabbedModal, setTabbedModal] = useState({ open: false, title: "", tabs: [] });

  const llms = (multiLLMs || []).filter(Boolean);
  const primaryMarket = ctx.markets[0];
  const selectedPersona = ctx.state.selectedPersona;
  const transactionStatus = ctx.state.transactionStatus;

  const _defaultComps = ctx.competitors?.["default"] ?? [];
  const categoryComps = ctx.competitors?.[category] ?? [];
  const headers = [
    ..._defaultComps.map((item) => item.name),
    ...categoryComps.map((item) => item.name),
  ];
  const totalBrandColumns = _defaultComps.length + categoryComps.length;

  // ── Compute composite data ──────────────────────────────────────────────────

  const allLLMData = llms.map((llm) => ({
    llm,
    data: ctx.category_dimensions?.[llm]?.[category]?.[primaryMarket]?.[selectedPersona],
  })).filter((d) => d.data?.data?.length > 0);

  let loading = true;
  let hasError = false;

  if (allLLMData.length > 0) {
    loading = false;
  } else if (transactionStatus === "RETRY") {
    hasError = true;
    loading = false;
  } else if (transactionStatus === "RUNNING") {
    loading = true;
  } else {
    loading = false;
  }

  // Build a map: canonicalAttributeName → Map<brandName, { scores: number[], explanations: [{llm, score, explanation}] }>
  const attrBrandMap = new Map(); // canonical attr name → { brandData: Map<brandName, {scores, perLLM}> }
  const attrLLMSet   = new Map(); // canonical attr name → Set<llm> (how many LLMs surfaced this attribute)

  allLLMData.forEach(({ llm, data }) => {
    (data.data || []).forEach((criteriaRow) => {
      const rawName = criteriaRow.name || "";
      const canonical = getCanonicalAttributeName(rawName);

      if (!attrBrandMap.has(canonical)) {
        attrBrandMap.set(canonical, new Map());
      }
      if (!attrLLMSet.has(canonical)) attrLLMSet.set(canonical, new Set());
      attrLLMSet.get(canonical).add(llm);
      const brandMap = attrBrandMap.get(canonical);

      (criteriaRow.brands || []).forEach((brandEntry) => {
        const bName = brandEntry.name || "";
        if (!brandMap.has(bName)) {
          brandMap.set(bName, { scores: [], perLLM: [] });
        }
        const entry = brandMap.get(bName);
        const runs = Array.isArray(brandEntry.multi_prompt_runs) ? brandEntry.multi_prompt_runs : null;
        const runScores = runs
          ? runs
              .map((run) => run?.rating ?? run?.score)
              .filter((v) => v != null && v !== "")
              .map(Number)
          : [];
        if (runScores.length > 0) {
          entry.scores.push(...runScores);
        } else if (brandEntry.rating != null && brandEntry.rating !== "") {
          entry.scores.push(Number(brandEntry.rating));
        }
        entry.perLLM.push({
          llm,
          score: brandEntry.rating,
          explanation: brandEntry.explanation,
        });
      });
    });
  });

  // Convert to flat array of composite rows
  const compositeRows = [...attrBrandMap.entries()].map(([attrName, brandMap]) => {
    const brands = headers.map((brandName) => {
      const entry = brandMap.get(brandName) || { scores: [], perLLM: [] };
      const avgRating = entry.scores.length > 0
        ? entry.scores.reduce((a, b) => a + b, 0) / entry.scores.length
        : null;
      return {
        name: brandName,
        rating: avgRating !== null ? Math.round(avgRating) : null,
        perLLM: entry.perLLM,
      };
    });
    return { name: attrName, brands, llmCount: attrLLMSet.get(attrName)?.size ?? 0 };
  });

  const totalLLMs = allLLMData.length;

  // Plain header renderer for the detail modal — no info icons, no modal toggle
  function renderDetailHeaders() {
    const headerDefs = [
      { id: "criteria", name: ENTITY_LABELS.purchaseCriteria },
      ..._defaultComps.map((item) => ({ id: item.name, name: item.name })),
      ...categoryComps.map((item) => ({ id: item.name, name: item.name })),
    ];
    return (
      <TableHead className={classes.table_head}>
        <TableRow>
          {headerDefs.map((header, index) => {
            const ctxCompetitor = index === 0 ? null
              : index <= _defaultComps.length
                ? _defaultComps[index - 1]
                : categoryComps[index - 1 - _defaultComps.length];
            const domain = ctxCompetitor?.domain ? extractDomain(ctxCompetitor.domain) : "";
            return (
              <TableCell
                key={index}
                className={index === 0 ? classes.table_head_cell_index : classes.table_head_cell}
              >
                <div className={classes.table_head_content}>
                  {index !== 0 ? (
                    <>
                      <BrandAvatar domainName={domain} brandName={header.name} />
                      <div className={classes.table_head_text}>{header.name}</div>
                    </>
                  ) : (
                    <div className={classes.table_head_text_comp}>
                      <span>{header.name}</span>
                    </div>
                  )}
                </div>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  // ── Render helpers ─────────────────────────────────────────────────────────

  function openBaseResponseModal(brandName) {
    const tabs = allLLMData.map(({ llm, data }) => ({
      label: getLLMLabel(llm),
      content: formatCategoryDimensionContent(data.baseResponses || {}, brandName, entity),
    }));
    setTabbedModal({
      open: true,
      title: `${ENTITY_LABELS.purchaseCriteria} - ${brandName}`,
      tabs,
    });
  }

  function renderHeaders() {
    const headerDefs = [
      { id: "criteria", name: ENTITY_LABELS.purchaseCriteria },
      ..._defaultComps.map((item) => ({ id: item.name, name: item.name })),
      ...categoryComps.map((item) => ({ id: item.name, name: item.name })),
    ];

    const hasBaseResponses = allLLMData.some(({ data }) => {
      const br = data.baseResponses;
      return br && typeof br === "object" && Object.keys(br).length > 0;
    });

    return (
      <TableHead className={classes.table_head}>
        <TableRow>
          {headerDefs.map((header, index) => {
            let ctxCompetitor = null;
            if (index !== 0) {
              if (index <= _defaultComps.length) {
                ctxCompetitor = _defaultComps[index - 1];
              } else {
                ctxCompetitor = categoryComps[index - 1 - _defaultComps.length];
              }
            }
            const domain = ctxCompetitor?.domain ? extractDomain(ctxCompetitor.domain) : "";

            return (
              <TableCell
                key={index}
                className={index === 0 ? classes.table_head_cell_index : classes.table_head_cell}
              >
                <div className={classes.table_head_content}>
                  {index !== 0 ? (
                    <>
                      <div className={classes.logo_container}>
                        <BrandAvatar domainName={domain} brandName={header.name} />
                        {!isCompanyUser && hasBaseResponses && (
                          <div className={classes.info_icon_wrapper}>
                            <Tooltip title="View Base Prompts (All LLMs)" arrow placement="top">
                              <IconButton
                                size="small"
                                onClick={() => openBaseResponseModal(header.name)}
                                sx={{
                                  padding: "1px",
                                  backgroundColor: "rgba(255,255,255,0.95)",
                                  color: "rgba(0,0,0,0.54)",
                                  border: "1px solid rgba(0,0,0,0.08)",
                                  "&:hover": {
                                    backgroundColor: "#2196F3",
                                    color: "#fff",
                                    border: "1px solid #2196F3",
                                  },
                                  boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                                }}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: "13px" }} />
                              </IconButton>
                            </Tooltip>
                          </div>
                        )}
                      </div>
                      <div className={classes.table_head_text}>{header.name}</div>
                    </>
                  ) : (
                    <div className={classes.table_head_text_comp}>
                      <div className={classes.table_head_title}>
                        <span>{header.name}</span>
                        {!showDetailModal && (
                          <MUIIconButton onClick={() => setShowDetailModal(true)}>
                            <InfoOutlined />
                          </MUIIconButton>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  function renderRows(rowItem, tableIndex) {
    return (
      <StyledTableRow key={tableIndex}>
        <StyledTableCell className={classes.attribute_headings}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {rowItem.name}
            {totalLLMs > 1 && rowItem.llmCount > 0 && (
              <ConsensusBadge llmCount={rowItem.llmCount} totalLLMs={totalLLMs} />
            )}
          </span>
        </StyledTableCell>
        {rowItem.brands.map((brandEntry, idx) => {
          const hasRating = brandEntry.rating !== null && brandEntry.rating !== undefined;
          let color = "#dc3545";
          if (!hasRating) { color = "#000"; }
          else if (brandEntry.rating >= 9) { color = "#198754"; }
          else if (brandEntry.rating >= 7) { color = "#2196F3"; }

          const tooltipContent = (
            <div style={{ minWidth: 160 }}>
              {brandEntry.perLLM.map(({ llm, score, explanation }, li) => (
                <div key={li} style={{ marginBottom: li < brandEntry.perLLM.length - 1 ? 8 : 0 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.75rem", color: "#a8c4f0", marginBottom: 2 }}>
                    {getLLMLabel(llm)}
                    {score != null ? ` — ${Number(score).toFixed(0)}` : " — N/A"}
                  </div>
                  {explanation && (
                    <div style={{ fontSize: "0.72rem", opacity: 0.9, lineHeight: 1.4 }}>
                      {explanation}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );

          const cell = (
            <StyledTableCell
              key={idx}
              style={{ color }}
              className={classes.attribute_score}
            >
              {hasRating ? brandEntry.rating : "-"}
            </StyledTableCell>
          );

          return hasRating ? (
            <Tooltip
              key={idx}
              title={tooltipContent}
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#1e2340",
                    fontSize: "0.73rem",
                    borderRadius: "10px",
                    padding: "9px 12px",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.28)",
                    "& .MuiTooltip-arrow": { color: "#1e2340" },
                  },
                },
              }}
              slotProps={{ popper: { modifiers: [{ name: "offset", options: { offset: [0, -18] } }] } }}
            >
              {cell}
            </Tooltip>
          ) : (
            cell
          );
        })}
      </StyledTableRow>
    );
  }

  function LoadingSkeleton() {
    return (
      <div className="p-3">
        <Skeleton variant="rectangular" height={30} />
        {Array(10).fill(0).map((_, i) => (
          <Skeleton key={i} variant="rectangular" width={i % 2 === 0 ? "70%" : "97%"} className="my-3" height={20} />
        ))}
      </div>
    );
  }

  return (
    <>
      <MUICard variant="elevated" elevation={0} className={classes.card}>
        {!loading && !hasError && compositeRows.length > 0 && (
          <>
            <div className={classes.table_scroll_wrapper}>
              <div style={{ minWidth: `calc(200px + ${totalBrandColumns} * 130px)` }}>
                <Table
                  elevation={0}
                  data={compositeRows}
                  renderHeaders={() => renderHeaders()}
                  render={renderRows}
                />
              </div>
            </div>

            <CADetailedTableModal
              show={showDetailModal}
              onClose={() => setShowDetailModal(false)}
              tabs={allLLMData.map(({ llm, data }) => ({
                label: getLLMLabel(llm),
                data: data.data || [],
                headers,
                renderHeaders: renderDetailHeaders,
                formatName: (name) => name,
                pdfTitle: `${ENTITY_LABELS.purchaseCriteria} - ${getLLMLabel(llm)}`,
                classes,
              }))}
            />
          </>
        )}
        {loading && !hasError && <LoadingSkeleton />}
        {!loading && !hasError && allLLMData.length === 0 && (
          <div style={{ padding: 16, color: "#888", fontSize: "0.9rem" }}>
            No data available across selected LLMs.
          </div>
        )}
      </MUICard>

      <CATabbedModal
        open={tabbedModal.open}
        title={tabbedModal.title}
        tabs={tabbedModal.tabs}
        onClose={() => setTabbedModal((s) => ({ ...s, open: false }))}
      />
    </>
  );
}
